import { GetRecurringPaymentResponse } from '@ce-openapi/business-subscription-service-ts-axios-client';
import { ProfileModel } from '@pukka/api';
import { storage } from '@pukka/storage';
import { differenceInCalendarDays, isAfter, isSameDay, subMonths } from 'date-fns';

import { calculateDaysLeft } from './calculateDaysLeft';

type ReminderDetail = {
  readonly type: 'expired' | 'reminder' | 'discount' | 'recurring';
  readonly text: string;
};

function getBillingReminderDetail(
  user?: ProfileModel.Response,
  isBannerOpen?: boolean,
  activeRecurring?: GetRecurringPaymentResponse,
  enableRecurringPayment = false,
): ReminderDetail | null {
  if (!user?.business) {
    return null;
  }

  const { business, is_owner: isOwner, already_subscribed: isAlreadySubscribed } = user;
  const { is_trial: isTrial, expired_at: expiredAt, has_expired: hasExpired, current_plan: currentPlan } = business;

  const today = new Date();
  const expiredDate = expiredAt ? new Date(expiredAt) : today;

  const daysLeft = calculateDaysLeft(expiredDate, today);

  if (hasExpired) {
    return {
      type: 'expired',
      text: '',
    };
  }

  if (isTrial && !isAlreadySubscribed) {
    return {
      type: 'reminder',
      text: daysLeft > 14 ? 'You are in trial mode' : `Your trial will end in ${daysLeft} day(s)`,
    };
  }

  const isOnBillingPage = window.location.pathname?.startsWith('/v2/settings/billing');

  if (enableRecurringPayment && activeRecurring) {
    const nextBillingDate = activeRecurring.next_billing_date;

    if (nextBillingDate) {
      const daysDiff = differenceInCalendarDays(new Date(nextBillingDate), new Date());

      if (daysDiff > 0 && daysDiff <= 7 && !isOnBillingPage && isOwner && isBannerOpen) {
        return {
          type: 'recurring',
          text: '',
        };
      }
    }

    return null;
  }

  const hasAnnualPlan = currentPlan === 12;

  if (!hasAnnualPlan && !isTrial && daysLeft <= 14) {
    return {
      type: 'reminder',
      text: `Your subscription will expire in ${daysLeft} day(s)`,
    };
  }

  const minusSixMonth = subMonths(expiredDate, 6);
  const isSameDayOrAfterM6 = isSameDay(today, minusSixMonth) || isAfter(today, minusSixMonth);
  const hasCloseBillingDiscountBanner = storage.get('hasCloseBillingDiscountBanner');
  const isDiscountBannerClosed = hasCloseBillingDiscountBanner || !isBannerOpen;
  const hasBillingDiscount = !isDiscountBannerClosed && isSameDayOrAfterM6 && !isOnBillingPage;

  if (isOwner && hasAnnualPlan && !isTrial && hasBillingDiscount) {
    return {
      type: 'discount',
      text: '',
    };
  }

  return null;
}

export { getBillingReminderDetail, type ReminderDetail };
